import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { faMoneyBill, faWallet } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWithdrawalsMutations } from '../../hooks/useWithdrawalsMutations';
import type { Withdrawal } from '../../models';

export interface MarkPaidWithdrawalModalRef {
  open: (withdrawal: Withdrawal) => void;
  close: () => void;
}

interface MarkPaidWithdrawalModalProps {
  onSuccess?: () => void;
}

const formatAmount = (value?: number | null, currencyCode?: string) => {
  if (value === undefined || value === null) return '—';
  return formatCurrencyAmount(value, currencyCode || 'EUR');
};

export const MarkPaidWithdrawalModal = forwardRef<MarkPaidWithdrawalModalRef, MarkPaidWithdrawalModalProps>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('withdrawalsModule');
    const { toast } = useToast();
    const currency = useCurrency();
    const modalRef = useRef<ModalCenterRef>(null);
    const { markPaid } = useWithdrawalsMutations();

    const [withdrawal, setWithdrawal] = useState<Withdrawal | null>(null);
    const [approvedAmount, setApprovedAmount] = useState('');
    const availableBalance = withdrawal?.wallet?.balance_total;
    const availableBalanceClass = (availableBalance ?? 0) > 0 ? 'text-warning' : 'text-error';

    useImperativeHandle(ref, () => ({
      open: (w) => {
        setWithdrawal(w);
        setApprovedAmount(w.approved_amount?.toString() || w.requested_amount.toString());
        modalRef.current?.openModal();
      },
      close: () => modalRef.current?.closeModal(),
    }));

    const handleConfirm = async () => {
      if (!withdrawal) return;
      try {
        await markPaid.mutateAsync({
          id: withdrawal.id,
          payload: approvedAmount ? { approved_amount: Number(approvedAmount) } : undefined,
        });
        toast({
          title: t('modals.markPaid.success.title'),
          description: t('modals.markPaid.success.description'),
        });
        modalRef.current?.closeModal();
        onSuccess?.();
      } catch (error) {
        toast({
          title: t('errors.markPaidFailed'),
          description: getErrorMessage(error),
          variant: 'destructive',
        });
      }
    };

    const isLoading = markPaid.isPending;

    return (
      <ModalCenter ref={modalRef} title={t('modals.markPaid.title')} width="lg">
        <div className="space-y-4">
          <p className="text-sm text-foreground">{t('modals.markPaid.description')}</p>

          {withdrawal && (
            <div className="bg-card-bg rounded-lg p-3 space-y-2">
              <p className="text-sm font-bold flex items-center justify-between gap-3">
                <span className="font-medium inline-flex items-center gap-1.5">
                  <FontAwesomeIcon icon={faMoneyBill} className="w-3.5 h-3.5" />
                  {t('modals.markPaid.fields.requested_amount')}:
                </span>
                <span>{formatAmount(withdrawal.requested_amount, currency)}</span>
              </p>
              <div className="border-t border-lines" />
              <p className={`text-sm font-bold flex items-center justify-between gap-3 ${availableBalanceClass}`}>
                <span className="inline-flex items-center gap-1.5">
                  <FontAwesomeIcon icon={faWallet} className="w-3.5 h-3.5" />
                  {t('modals.markPaid.fields.available_balance')}:
                </span>
                <span>{formatAmount(availableBalance, currency)}</span>
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('modals.markPaid.fields.approved_amount')}</label>
            <Input
              type="number"
              value={approvedAmount}
              onChange={(e) => setApprovedAmount(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => modalRef.current?.closeModal()} disabled={isLoading}>
              {t('buttons.cancel')}
            </Button>
            <Button variant="primary" onClick={handleConfirm} disabled={isLoading}>
              {isLoading ? t('buttons.marking') : t('actions.markPaid')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

MarkPaidWithdrawalModal.displayName = 'MarkPaidWithdrawalModal';
